import { merge, isEmpty } from 'lodash';

import { MonitoringBackEndDataSourceModel } from '@Monitor/Shared/models/monitoring-back-end-data-source.model';
import { MonitoringSeriesTypeEnum } from '@Monitor/Shared/enums/monitoring-series-type.enum';
import { BriefingTextDisplayElementModel } from './briefing-text-display-element.model';
import { CoverageInsights } from '../../Shared/models/coverage-insights.model';
import { MentionSearchModel } from '@Monitor/Shared/models/mention-search.model';
import { BriefingAnalysisDisplayElementApiModel } from '@api/briefings/model/briefing-analysis-display-element-api.model';
import { MentionSourceApiTypesEnum,
    mentionSourceApiTypesEnumToMonitoringSeriesTypeEnum,
    monitoringSeriesTypeEnumToMentionSourceApiTypesEnum } from '@api/mentions/enum/mention-source-api-types.enum';
import { DisplayElementWithSearchCriteriaModel } from './display-element-with-search-criteria.model';


export class AnalysisDisplayElementModel extends BriefingTextDisplayElementModel implements DisplayElementWithSearchCriteriaModel {

    backendDataSource: MonitoringBackEndDataSourceModel;
    analysisField: CoverageInsights;
    generateOnSend: boolean;
    filterMentionSearchModel: MentionSearchModel;
    initialSourceMentionSearchModel: MentionSearchModel;
    searchCriteriaId: number;
    sourceBlockReferenceId: string;
    sourceType: MentionSourceApiTypesEnum = MentionSourceApiTypesEnum.COVERAGE_IN_SECTION;
    readonly dateRangeAllowed: boolean = true;
    curatedMentionCount?: number;
    dynamicMentionCount?: number;
    loading?: boolean;
    useSearchDateFilter: boolean;

    constructor(
        content?: string,
        styles?: any,
        apiModel?: BriefingAnalysisDisplayElementApiModel
    ) {
        super(content, styles);
        if (apiModel) {
            this.generateOnSend = apiModel.generateOnSend;

            this.backendDataSource = new MonitoringBackEndDataSourceModel(
                mentionSourceApiTypesEnumToMonitoringSeriesTypeEnum.get(apiModel.sourceType),
                apiModel.searchCriteriaId
            );

            this.sourceType = apiModel.sourceType;

            this.searchCriteriaId = apiModel.filterSearchCriteriaId;
            this.analysisField = new CoverageInsights({bulkAnalysisMessage: apiModel.bulkAnalysisMessage});
            if (apiModel.sourceBlockReferenceId) {
                this.sourceBlockReferenceId = apiModel.sourceBlockReferenceId;
                this.backendDataSource.referenceId = apiModel.sourceBlockReferenceId;
            }

            this.automaticallyGenerated = apiModel.automaticallyGenerated;
            this.useSearchDateFilter = apiModel.useSearchDateFilter;

        } else {
            this.backendDataSource = new MonitoringBackEndDataSourceModel(MonitoringSeriesTypeEnum.coverageInSection);
            this.sourceType = MentionSourceApiTypesEnum.COVERAGE_IN_SECTION;
            this.generateOnSend = false;
        }
    }

    clone(): AnalysisDisplayElementModel {
        let newModel = new AnalysisDisplayElementModel(
            this.text,
            this.styles.clone(),
            this.toApiModel()
        );
        const clone = super.clone() as AnalysisDisplayElementModel;

        newModel = merge(newModel, clone);

        newModel.backendDataSource = this.backendDataSource?.clone();
        newModel.filterMentionSearchModel = this.filterMentionSearchModel?.clone();
        newModel.analysisField = this.analysisField?.clone();
        newModel.sourceBlockReferenceId = this.sourceBlockReferenceId;
        newModel.sourceType = this.sourceType;
        newModel.canAutomaticallyGenerated = this.canAutomaticallyGenerated;
        newModel.automaticallyGenerated = this.automaticallyGenerated;
        newModel.useSearchDateFilter = this.useSearchDateFilter;
        clone.initialSourceMentionSearchModel = this.initialSourceMentionSearchModel?.clone();

        return newModel;
    }

    toApiModel(): BriefingAnalysisDisplayElementApiModel {
        const analysisDisplayElement = this as AnalysisDisplayElementModel;

        // // hack @TODO NEP-3392
        const styles = analysisDisplayElement.styles.toBlockStyle();
        styles.isBoundToGlobalStyles = analysisDisplayElement.isBoundToGlobalStyles;
        styles.htmlEdit = analysisDisplayElement.htmlEdit;
        // // endhack

        const analysisBlock = {
            styles: JSON.stringify(styles),
            customStyleId: styles && Number.isInteger(styles.styleState) ? styles.styleState : null,
            generateOnSend: analysisDisplayElement.generateOnSend,
            sourceType: monitoringSeriesTypeEnumToMentionSourceApiTypesEnum.get(analysisDisplayElement.backendDataSource.type),
            automaticallyGenerated: this.automaticallyGenerated,
            useSearchDateFilter: this.useSearchDateFilter
        } as unknown as BriefingAnalysisDisplayElementApiModel;

        analysisBlock.bulkAnalysisMessage = analysisDisplayElement.analysisField?.toApiModel()?.bulkAnalysisMessage;
        if (!analysisBlock.generateOnSend && analysisDisplayElement.text?.length) {
            analysisBlock.value = analysisDisplayElement.text;
        }

        if (analysisDisplayElement.backendDataSource.type === MonitoringSeriesTypeEnum.coverageInSection) {
            if (analysisDisplayElement.backendDataSource.referenceId) {
                analysisBlock.sourceBlockReferenceId = analysisDisplayElement.backendDataSource.referenceId;
            } else {
                analysisBlock.sourceBlockReferenceId = analysisDisplayElement.sourceBlockReferenceId;
            }
        } else {
            if (analysisDisplayElement.backendDataSource.type !== MonitoringSeriesTypeEnum.allCuratedCoverageInBrief) {
                analysisBlock.filterSearchCriteriaId = analysisDisplayElement.searchCriteriaId;
                analysisBlock.searchCriteriaId = analysisDisplayElement.backendDataSource.id;
            }
        }
        return analysisBlock;
    }

    canRender(): boolean {
        // might have to change here but we'll see
        if (!this.text) {
            return false;
        }

        return true;
    }

    onUpdate(): boolean {
        if (this.backendDataSource && this.backendDataSource.type) {
            this.sourceType = monitoringSeriesTypeEnumToMentionSourceApiTypesEnum.get(this.backendDataSource.type);
            if (!this.filterMentionSearchModel) {
                this.filterMentionSearchModel = new MentionSearchModel();
            }

            this.filterMentionSearchModel.searchCriteria = this.backendDataSource.filterSearchCriteria;
        }
        return false;
    }

    isEqual(other: AnalysisDisplayElementModel): boolean {
        // If the block is using a saved style, just compare that it's still using the same saved style, if it's not using a saved style, compare the values
        if (this.styles.styleState &&
            Number.isInteger(this.styles.styleState)
        ) {
            if (this.styles.styleState !== other.styles?.styleState) {
                return false;
            }
        } else if (!this.styles.equals(other.styles, true)) {
            return false;
        }

        // emtpy string & undefined should still "match"
        if (this.text !== other.text && !(isEmpty(this.text) && isEmpty(other.text))) {
            return false;
        }

        if (!this.backendDataSource.equals(other.backendDataSource, false)) {
            return false;
        }

        if (this.isBoundToGlobalStyles !== other.isBoundToGlobalStyles) {
            return false;
        }

        if (this.sourceType !== other.sourceType) {
            return false;
        }
        if (this.generateOnSend !== other.generateOnSend) {
            return false;
        }
        if (this.sourceBlockReferenceId !== other.sourceBlockReferenceId) {
            return false;
        }
        if (!this.analysisField.equals(other.analysisField)) {
            return false;
        }

        if (this.automaticallyGenerated !== other.automaticallyGenerated) {
            return false;
        }

        if (this.useSearchDateFilter !== other.useSearchDateFilter) {
            return false;
        }

        return true;
    }

    setSearchCriteria(searchCriteria: MentionSearchModel) {
        this.filterMentionSearchModel = searchCriteria;
        this.backendDataSource.filterSearchCriteria = this.filterMentionSearchModel.searchCriteria;
        this.backendDataSource.sourceSearchCriteria.query.dateQuery = searchCriteria.searchCriteria.query.dateQuery;
    }

    getSearchCriteria(): MentionSearchModel {
        if (this.sourceType === MentionSourceApiTypesEnum.COVERAGE_IN_SECTION || this.sourceType === MentionSourceApiTypesEnum.ALL_CURATED_COVERAGE) {
            return null;
        }
        if (!this.filterMentionSearchModel) {
            return null;
        }
        this.filterMentionSearchModel.searchCriteria = this.backendDataSource.filterSearchCriteria;
        if (!this.filterMentionSearchModel.searchCriteria?.query) {
            return null;
        }
        // some reason the date is being stored in the sourceSearchCriteria, and not filterSearchCriteria
        this.filterMentionSearchModel.searchCriteria.query.dateQuery = this.backendDataSource.sourceSearchCriteria?.query.dateQuery;

        return this.filterMentionSearchModel;
    }

    setInitialSourceSearchCriteria(searchCriteria: MentionSearchModel) {
        this.initialSourceMentionSearchModel = searchCriteria;
    }
}
